import { cn } from '@/ui/lib/utils'
import { DateField } from '@/ui/DateField'
import { Select } from '@/ui/Select'

export type QuantParamKind = 'int' | 'float' | 'bool' | 'choice' | 'str'

export interface QuantParamRowProps {
  /** Param name, shown as-is (never uppercased) and used as the control's accessible name. */
  name: string
  kind: QuantParamKind
  /** Held value: a boolean for `bool`, otherwise the raw text. */
  value: string | number | boolean
  /** Choices, only for `choice`. */
  options?: string[]
  /** Spec 073: the display label of a choice (`NQ (Databento)`); the value stays the raw option. */
  optionLabel?: (option: string) => string
  help?: string
  /** True when the value differs from the strategy.toml default. */
  changed: boolean
  /** Printable default, shown in the CHANGED marker. */
  defaultLabel: string
  error?: string
  onChange: (next: string | boolean) => void
  /** `modal` (the indicator modal): label-only left side, no type tag/hairline, 150px control. */
  variant?: 'config' | 'modal'
  /** `date` renders a `DateField` (DD.MM.YYYY text plus calendar; the held value stays the raw `YYYY-MM-DD` text). */
  inputType?: 'date'
  /** Inclusive ISO date bounds, only used with `inputType="date"`; `null`/omitted is open on that side. */
  min?: string | null
  max?: string | null
  /** A non-blocking amber note (spec 039 8.3); never an error and never marks the field invalid. */
  warning?: string
  /** Spec 041: `account` rows (the Paper account block) carry `data-account-row` instead of `data-param-row`. */
  section?: 'param' | 'account'
  /** Spec 041: a short suffix on the first meta line (for example `not used live`); dims the row, adds no height, keeps the control editable. */
  muted?: string
}

/**
 * One parameter row of the Config form (spec 036 handoff 3.3 item 3), on a
 * paper surface. Left: name, type tag, `CHANGED · DEFAULT x`, help, inline
 * error. Right: a control chosen by type (On/Off segment, select, or text
 * input whose `inputMode` follows the type so bad input can reach the
 * validator). Reusable by the Manual-trade indicator modal.
 */
export function QuantParamRow({
  name,
  kind,
  value,
  options = [],
  optionLabel,
  help,
  changed,
  defaultLabel,
  error,
  onChange,
  variant = 'config',
  inputType,
  min,
  max,
  warning,
  section = 'param',
  muted,
}: QuantParamRowProps) {
  const modal = variant === 'modal'
  const segment = (on: boolean) =>
    cn(
      'flex-1 border-0 py-[7px] font-sans text-[10.5px] font-semibold tracking-[0.2em] uppercase',
      on ? 'bg-ink text-paper' : 'bg-transparent text-ink-50',
    )

  return (
    <div
      {...(section === 'account' ? { 'data-account-row': name } : { 'data-param-row': name })}
      data-muted={muted !== undefined ? '' : undefined}
      className={cn(
        'grid items-center',
        muted !== undefined && 'opacity-60',
        modal
          ? 'grid-cols-[minmax(0,1fr)_150px] gap-x-3.5 gap-y-1'
          : 'grid-cols-[minmax(0,1fr)_minmax(150px,230px)] gap-x-5 gap-y-1.5 border-b border-ink-14 py-[9px]',
      )}
    >
      <div className="flex min-w-0 flex-col gap-[3px]">
        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
          <span className={cn('font-mono font-semibold [overflow-wrap:anywhere]', modal ? 'text-[11.5px]' : 'text-[12.5px]')}>
            {name}
          </span>
          {!modal && <span className="font-mono text-[9px] tracking-[0.14em] text-ink-50 uppercase">{kind}</span>}
          {muted !== undefined && (
            <span className="font-mono text-[9px] tracking-[0.12em] text-ink-50 uppercase">{muted}</span>
          )}
          {changed && (
            <span className="font-mono text-[9px] tracking-[0.12em] text-accent-fill uppercase">
              changed · default {defaultLabel}
            </span>
          )}
        </div>
        {help && (
          <div className="font-mono text-[11px] leading-[1.45] text-ink-50 [text-wrap:pretty]">{help}</div>
        )}
        {error && inputType !== 'date' && (
          <div role="alert" className="font-mono text-[11px] text-alert">
            {error}
          </div>
        )}
        {inputType === 'date' && (
          // The line is always reserved on a date row so an error or a warning appearing moves nothing.
          <div
            role={error ? 'alert' : warning ? 'status' : undefined}
            className={cn('h-4 truncate font-mono text-[11px]', error ? 'text-alert' : 'text-[#CB8A00]')}
            title={error ?? warning}
          >
            {error ?? warning}
          </div>
        )}
      </div>
      {kind === 'bool' && (
        <div role="group" aria-label={name} className="flex border border-ink-25">
          <button type="button" aria-pressed={value === true} onClick={() => onChange(true)} className={segment(value === true)}>
            On
          </button>
          <button type="button" aria-pressed={value !== true} onClick={() => onChange(false)} className={segment(value !== true)}>
            Off
          </button>
        </div>
      )}
      {kind === 'choice' && (
        <Select
          ariaLabel={name}
          value={String(value)}
          onValueChange={(next) => onChange(next)}
          options={options.map((option) => ({ value: option, label: optionLabel?.(option) ?? option }))}
          className="min-w-0 px-[9px] py-[7px] text-[12.5px] text-ink"
        />
      )}
      {kind !== 'bool' && kind !== 'choice' && inputType === 'date' && (
        <DateField
          ariaLabel={name}
          value={String(value)}
          min={min}
          max={max}
          invalid={error !== undefined}
          onChange={(next) => onChange(next)}
        />
      )}
      {kind !== 'bool' && kind !== 'choice' && inputType !== 'date' && (
        <input
          type="text"
          aria-label={name}
          aria-invalid={error ? true : undefined}
          inputMode={kind === 'int' ? 'numeric' : kind === 'float' ? 'decimal' : 'text'}
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
          className={cn(
            'min-w-0 border bg-transparent px-[9px] py-[7px] font-mono text-[12.5px] text-ink outline-none focus:border-accent-fill',
            error ? 'border-alert' : 'border-ink-25',
          )}
        />
      )}
    </div>
  )
}
